import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { useSlowQueries, useResetSlowQueriesStats } from "@/hooks/useSlowQueries";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, RefreshCw, Trash2, Activity, AlertTriangle } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { SidebarTrigger } from "@/components/ui/sidebar";

const fmtNum = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

const fmtInt = (n: number) =>
  new Intl.NumberFormat("en-US").format(n);

function severityBadge(meanMs: number) {
  if (meanMs >= 2000) return <Badge variant="destructive">Crítico</Badge>;
  if (meanMs >= 1000) return <Badge className="bg-amber-500 hover:bg-amber-600 text-white">Alto</Badge>;
  return <Badge variant="secondary">Medio</Badge>;
}

export default function Performance() {
  const navigate = useNavigate();
  const { isSuperAdmin, isLoading: checkingAdmin } = useIsSuperAdmin();

  useEffect(() => {
    if (!checkingAdmin && !isSuperAdmin) {
      navigate("/dashboard/resumen", { replace: true });
    }
  }, [isSuperAdmin, checkingAdmin, navigate]);

  const { data: rows, isLoading, isFetching, error, refetch } = useSlowQueries(isSuperAdmin);
  const resetMut = useResetSlowQueriesStats();

  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isSuperAdmin) return null;

  return (
    <AppShell>
      <header className="h-12 flex items-center border-b bg-background sticky top-0 z-10">
        <SidebarTrigger className="ml-2" />
      </header>
      <div className="flex-1 bg-background p-6">
        <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Activity className="h-6 w-6 text-primary" />
              Database Performance
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Consultas SQL con tiempo medio de ejecución &gt; 500&nbsp;ms.
              Auto-refresh cada 30s.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
              Refrescar
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={resetMut.isPending}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset Stats
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    Resetear pg_stat_statements
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Esto borra el histórico acumulado de todas las consultas SQL.
                    Útil después de aplicar un nuevo índice para medir la mejora desde cero.
                    <br /><br />
                    <strong>Esta acción no se puede deshacer.</strong>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => resetMut.mutate()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Sí, resetear
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* KPI ribbon */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Queries lentas</p>
              <p className="text-2xl font-semibold tabular-nums mt-1">
                {rows?.length ?? 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Peor mean</p>
              <p className="text-2xl font-semibold tabular-nums mt-1">
                {rows?.length ? `${fmtNum(rows[0].mean_ms)} ms` : "—"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Total calls</p>
              <p className="text-2xl font-semibold tabular-nums mt-1">
                {rows?.length ? fmtInt(rows.reduce((s, r) => s + Number(r.calls), 0)) : "—"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Umbral</p>
              <p className="text-2xl font-semibold tabular-nums mt-1">&gt; 500 ms</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Slow Queries</CardTitle>
            <CardDescription>
              Datos en vivo desde <code className="text-xs">pg_stat_statements</code>.
              Los promedios se acumulan desde el último reset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="text-destructive text-sm py-6">
                Error: {(error as Error).message}
              </div>
            ) : !rows || rows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Activity className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No hay consultas lentas (&gt; 500 ms) detectadas.</p>
                <p className="text-xs mt-1">El sistema está respondiendo dentro del SLA. ✓</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Sev.</TableHead>
                      <TableHead>Query</TableHead>
                      <TableHead className="text-right w-[90px]">Calls</TableHead>
                      <TableHead className="text-right w-[100px]">Mean (ms)</TableHead>
                      <TableHead className="text-right w-[100px]">Max (ms)</TableHead>
                      <TableHead className="text-right w-[110px]">Total (ms)</TableHead>
                      <TableHead className="text-right w-[80px]">Rows</TableHead>
                      <TableHead className="text-right w-[90px]">Cache %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r, idx) => (
                      <TableRow key={idx} className="text-[13px]">
                        <TableCell className="py-1.5">{severityBadge(r.mean_ms)}</TableCell>
                        <TableCell className="py-1.5 font-mono text-xs max-w-[600px] truncate" title={r.query_preview}>
                          {r.query_preview}
                        </TableCell>
                        <TableCell className="py-1.5 text-right tabular-nums">{fmtInt(r.calls)}</TableCell>
                        <TableCell className="py-1.5 text-right tabular-nums font-medium">{fmtNum(r.mean_ms)}</TableCell>
                        <TableCell className="py-1.5 text-right tabular-nums">{fmtNum(r.max_ms)}</TableCell>
                        <TableCell className="py-1.5 text-right tabular-nums">{fmtNum(r.total_ms)}</TableCell>
                        <TableCell className="py-1.5 text-right tabular-nums">{fmtInt(r.rows)}</TableCell>
                        <TableCell className="py-1.5 text-right tabular-nums">
                          {r.cache_hit_pct == null ? "—" : `${fmtNum(r.cache_hit_pct)}%`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>
    </AppShell>
  );
}
